import { FormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom, Observable } from "rxjs";
import { EntityDto } from "../../service/modal.service";
import { pendingChangesModal } from "../../guard/pending-changes.guard";

export abstract class BaseFormGroupService<Dto extends EntityDto> {
  abstract entitySingular: string;
  formGroup: FormGroup;
  entity: Dto;
  submitted: boolean = false;

  get editText(): string {
    return this.entitySingular + (this.entity?.id ? " bearbeiten" : " hinzufügen");
  }

  protected constructor(
    protected modal: NgbActiveModal,
    protected toastrService: ToastrService
  ) {}

  abstract initFormGroup(): void;

  protected abstract updateFormGroup(entity: Dto): void;

  protected getMergedEntity(): Dto {
    return this.mergeObjects(this.entity, this.formGroup.value, this.formGroup.getRawValue()) as Dto;
  }

  private mergeObjects(existingObject: Object, newObject: Object, rawNewObject: Object): Object {
    if (!existingObject) {
      return rawNewObject;
    }
    const mergedObject = { ...existingObject, ...newObject };
    for (const key in mergedObject) {
      if (Array.isArray(rawNewObject?.[key])) {
        //use raw object, so disabled entries are not lost
        const objectArray: Object[] = rawNewObject[key];
        objectArray.forEach((entry, idx) => {
          if (!entry["id"]) {
            return;
          }
          const existing = existingObject[key]?.find((existingEntry) => existingEntry["id"] === entry["id"]);
          objectArray[idx] = this.mergeObjects(existing, entry, entry);
        });
        mergedObject[key] = objectArray;
      } else if (existingObject[key] && existingObject[key]["id"]) {
        mergedObject[key] = this.mergeObjects(existingObject[key], newObject?.[key], rawNewObject?.[key]);
      }
    }
    return mergedObject;
  }

  validityCheck(): boolean {
    this.submitted = true;
    if (this.formGroup.invalid) {
      this.toastrService.error("Bitte füllen Sie alle Pflichtfelder aus.", "Speichern fehlgeschlagen");
      return false;
    }
    return true;
  }

  dirtyCheck(): boolean {
    if (this.formGroup.dirty) {
      this.toastrService.warning(
        "Speichern Sie Ihre Änderungen und probieren Sie es erneut.",
        "Ungespeicherte Änderungen"
      );
      return false;
    }
    return true;
  }

  async onSubmit(close: boolean = true): Promise<boolean> {
    if (!this.validityCheck()) {
      return false;
    }
    const mergedEntity: Dto = this.getMergedEntity();

    return new Promise((resolve) => {
      if (mergedEntity.id) {
        this.update(mergedEntity).subscribe({
          next: (entity) => {
            this.entityCreated(entity, close);
            resolve(true);
          },
          error: () => resolve(false),
        });
      } else {
        this.create(mergedEntity).subscribe({
          next: (entity) => {
            this.entityCreated(entity, close);
            resolve(true);
          },
          error: () => resolve(false),
        });
      }
    });
  }

  async saveIfDirty(): Promise<boolean> {
    if (!this.formGroup.dirty) {
      return true;
    }
    return this.onSubmit(false);
  }

  protected abstract create(mergedEntity: Dto): Observable<Dto>;
  protected abstract update(mergedEntity: Dto): Observable<Dto>;
  protected abstract reloadEntity(id: number): Observable<Dto>;

  async reload() {
    if (!this.dirtyCheck() || !this.entity?.id) {
      return;
    }
    const entity = await firstValueFrom(this.reloadEntity(this.entity.id));
    this.updateEntity(entity);
  }

  protected entityCreated(entity: Dto, close: boolean) {
    this.toastrService.success("", `${this.entitySingular} gespeichert`);
    this.submitted = false;
    if (close) {
      this.modal.close(entity);
      return;
    }
    this.updateEntity(entity);
  }

  updateEntity(entity: Dto) {
    this.entity = entity;
    this.updateFormGroup(entity);
    if (!entity.id) {
      this.formGroup.markAsDirty();
    } else {
      this.formGroup.markAsPristine();
    }
  }

  async closeModal() {
    if (!this.formGroup.dirty) {
      this.modal.dismiss();
      return;
    }
    const confirm = await pendingChangesModal();
    if (confirm.isConfirmed) {
      this.modal.dismiss();
    }
  }
}
